"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Check, Copy, CornerDownLeft, Play, Search, X } from "lucide-react";
import { DialogTitle } from "@/shadcn-components/ui/dialog";
import { cn } from "@/lib/utils";
import { useTerminalStore } from "@/lib/terminal/store";
import { vybeTraderTerminalDslEngine } from "@/lib/terminal/engine/VybeTraderTerminalDslEngine";
import {
  groupByNamespace,
  namespaceOf,
} from "@/lib/terminal/engine/namespace";
import type { DslListedFunction } from "@/lib/terminal/engine/types";

const ALL_NAMESPACES = "__all__";

/**
 * Derive a ready-to-run terminal template from a function's metadata.
 * Prefers the example (first line, before any "→ …" result), then falls
 * back to the signature's call head, then a bare `Path()`.
 */
function toInsertTemplate(fn: DslListedFunction): string {
  if (fn.example) {
    const firstLine = fn.example.split("\n")[0]?.trim() ?? "";
    const call = firstLine.split(/\s*(?:→|=>|=)\s*/)[0]?.trim();
    if (call) return call;
  }
  // Signatures carry param *types* (`symbol: string`) that aren't runnable,
  // so fall back to a bare call the user can fill in.
  return `${fn.path}()`;
}

export interface DslExplorerPanelProps {
  /**
   * "dialog" — full-screen 3-column layout inside DslExplorerDialog;
   * Insert/Run close the dialog via `onRequestClose`.
   * "docked" — narrower 2-column layout (nav + list, detail below);
   * Insert/Run do NOT close anything, and the header ✕ hides the pane.
   */
  variant: "dialog" | "docked";
  /** Dialog: close the dialog. Docked: hide the docked pane. */
  onRequestClose: () => void;
}

export default function DslExplorerPanel({
  variant,
  onRequestClose,
}: DslExplorerPanelProps) {
  const runCommand = useTerminalStore((s) => s.runCommand);
  const setInputDraft = useTerminalStore((s) => s.setInputDraft);
  const isDialog = variant === "dialog";

  // Snapshot the registry on mount. The modules barrel is imported
  // lazily by the engine; kick it so the list is fully populated.
  const [list, setList] = useState<DslListedFunction[]>(() =>
    vybeTraderTerminalDslEngine.list()
  );
  const [query, setQuery] = useState("");
  const [namespace, setNamespace] = useState<string>(ALL_NAMESPACES);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void import("@/lib/terminal/engine/modules").then(() => {
      if (!cancelled) setList(vybeTraderTerminalDslEngine.list());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => groupByNamespace(list), [list]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter((fn) => {
      if (namespace !== ALL_NAMESPACES && namespaceOf(fn) !== namespace) {
        return false;
      }
      if (!q) return true;
      return (
        fn.path.toLowerCase().includes(q) ||
        fn.description.toLowerCase().includes(q)
      );
    });
  }, [list, namespace, query]);

  // Keep a valid selection as filters change.
  const selected = useMemo(
    () => filtered.find((fn) => fn.path === selectedPath) ?? filtered[0] ?? null,
    [filtered, selectedPath]
  );

  const runTemplate = (fn: DslListedFunction) => {
    void runCommand(toInsertTemplate(fn));
    if (isDialog) onRequestClose();
  };
  const insertTemplate = (fn: DslListedFunction) => {
    setInputDraft(toInsertTemplate(fn));
    if (isDialog) onRequestClose();
  };
  const copyTemplate = (fn: DslListedFunction) => {
    void navigator.clipboard?.writeText(toInsertTemplate(fn));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  const detailPane = (
    <div className="min-h-0 overflow-auto p-4">
      {selected ? (
        <>
          <div className="font-mono text-[14px] text-foreground">
            {selected.path}
          </div>
          <div className="mt-1.5 break-all font-mono text-[11px] text-blue-400">
            {selected.signature}
          </div>
          <p className="mt-3 text-[12px] leading-relaxed text-muted-foreground">
            {selected.description}
          </p>

          <div className="mb-1.5 mt-4 font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
            Example
          </div>
          {selected.example ? (
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-card px-3 py-2 font-mono text-[11px] text-foreground">
              {selected.example}
            </pre>
          ) : (
            <div className="rounded-md border border-dashed border-border px-3 py-2 font-mono text-[11px] italic text-muted-foreground">
              No example provided — try{" "}
              <span className="not-italic text-foreground">
                {toInsertTemplate(selected)}
              </span>
            </div>
          )}

          <div className="mt-3.5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => runTemplate(selected)}
              className="inline-flex h-7 items-center gap-1.5 rounded-md bg-foreground px-3 font-mono text-[11px] font-medium text-background hover:opacity-90"
            >
              <Play className="h-3 w-3" />
              Run
            </button>
            <button
              type="button"
              onClick={() => insertTemplate(selected)}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-muted px-3 font-mono text-[11px] text-foreground hover:border-foreground/40"
            >
              <CornerDownLeft className="h-3 w-3" />
              Insert into terminal
            </button>
            <button
              type="button"
              onClick={() => copyTemplate(selected)}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-muted px-3 font-mono text-[11px] text-foreground hover:border-foreground/40"
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </>
      ) : (
        <div className="font-mono text-[11px] text-muted-foreground">
          Select a function to see its details.
        </div>
      )}
    </div>
  );

  const namespaceNav = (
    <nav className="overflow-auto border-r border-border bg-card py-1.5">
      <NamespaceItem
        label="All"
        count={list.length}
        active={namespace === ALL_NAMESPACES}
        onClick={() => setNamespace(ALL_NAMESPACES)}
      />
      {groups.map((g) => (
        <NamespaceItem
          key={g.name}
          label={g.name}
          count={g.fns.length}
          active={namespace === g.name}
          onClick={() => setNamespace(g.name)}
        />
      ))}
    </nav>
  );

  const functionList = (
    <div className="min-h-0 overflow-auto">
      {filtered.length === 0 ? (
        <div className="p-4 font-mono text-[11px] text-muted-foreground">
          No functions match “{query}”.
        </div>
      ) : (
        filtered.map((fn) => (
          <button
            key={fn.path}
            type="button"
            onClick={() => setSelectedPath(fn.path)}
            onDoubleClick={() => insertTemplate(fn)}
            className={cn(
              "flex h-[30px] w-full items-center gap-2 border-b border-border/60 px-3 text-left font-mono text-[11px] hover:bg-accent",
              selected?.path === fn.path && "bg-accent"
            )}
          >
            <FnPath path={fn.path} />
            <span
              className={cn(
                "ml-auto truncate text-[10px] text-muted-foreground",
                isDialog ? "max-w-[210px]" : "max-w-[120px]"
              )}
            >
              {fn.signature}
            </span>
          </button>
        ))
      )}
    </div>
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-card px-3">
        {isDialog ? (
          <DialogTitle className="text-[13px] font-semibold text-foreground">
            Explore DSL
          </DialogTitle>
        ) : (
          <span className="text-[13px] font-semibold text-foreground">
            Explore DSL
          </span>
        )}
        <span className="hidden font-mono text-[10px] text-muted-foreground sm:inline">
          {list.length} functions · {groups.length} namespaces
        </span>
        <div className="ml-auto flex h-6 min-w-[90px] flex-[0_1_14rem] items-center gap-1.5 rounded-md border border-border bg-muted px-2">
          <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
          <input
            autoFocus={isDialog}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or description…"
            className="w-0 flex-1 bg-transparent font-mono text-[11px] text-foreground placeholder:text-muted-foreground focus:outline-none"
            aria-label="Filter DSL functions"
          />
        </div>
        <button
          type="button"
          onClick={onRequestClose}
          aria-label={
            isDialog ? "Close DSL explorer" : "Hide DSL explorer"
          }
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {isDialog ? (
        /* Body: nav · list · detail */
        <div className="grid min-h-0 flex-1 grid-cols-[160px_1fr_320px]">
          {namespaceNav}
          <div className="min-h-0 overflow-auto border-r border-border">
            {functionList}
          </div>
          {detailPane}
        </div>
      ) : (
        /* Docked body: nav · list, detail below on-select */
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="grid min-h-0 flex-1 grid-cols-[130px_1fr]">
            {namespaceNav}
            {functionList}
          </div>
          <div className="max-h-[45%] shrink-0 overflow-auto border-t border-border">
            {detailPane}
          </div>
        </div>
      )}
    </div>
  );
}

function NamespaceItem({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-[26px] w-full items-center gap-2 px-3 text-left font-mono text-[11px] hover:bg-accent",
        active
          ? "bg-accent text-foreground shadow-[inset_2px_0_0_#FF6B35]"
          : "text-muted-foreground"
      )}
    >
      <span className="truncate">{label}</span>
      <span className="ml-auto text-[9px] text-muted-foreground">{count}</span>
    </button>
  );
}

/** Render a dotted path with the final segment emphasised. */
function FnPath({ path }: { path: string }) {
  const dot = path.lastIndexOf(".");
  if (dot === -1) return <span className="text-foreground">{path}</span>;
  return (
    <span className="text-muted-foreground">
      {path.slice(0, dot + 1)}
      <span className="text-foreground">{path.slice(dot + 1)}</span>
    </span>
  );
}
